import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { contactService } from '../../../services/contactService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { ContactCard } from '../../../components/app/contacts/ContactCard';
import { ContactFormModal } from '../../../components/app/contacts/ContactFormModal';
import { DeleteContactModal } from '../../../components/app/contacts/DeleteContactModal';
import { MergeContactsModal } from '../../../components/app/contacts/MergeContactsModal';
import { FollowUpModal } from '../../../components/app/followups/FollowUpModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const emailKey = (c) => (c.email || '').trim().toLowerCase();
const phoneKey = (c) => {
  const digits = (c.phone || '').replace(/[^0-9]/g, '');
  return digits.length >= 7 ? digits : '';
};

const findMatches = (contact, all) => {
  const e = emailKey(contact);
  const p = phoneKey(contact);
  if (!e && !p) return [];
  return all.filter((o) => o.id !== contact.id && ((e && emailKey(o) === e) || (p && phoneKey(o) === p)));
};

export const ContactsList = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(new Map());
  const [cardIds, setCardIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | connections | contacts
  const [formState, setFormState] = useState(null); // { contact } | null
  const [toDelete, setToDelete] = useState(null);
  const [toResolve, setToResolve] = useState(null);
  const [followUpFor, setFollowUpFor] = useState(null);

  const load = async () => {
    const [list, moments, cards] = await Promise.all([
      contactService.getContacts(),
      contactService.getMomentStats().catch(() => new Map()),
      contactService.getCardContactIds().catch(() => new Set())
    ]);
    setContacts(list);
    setStats(moments);
    setCardIds(cards);
  };

  useEffect(() => {
    if (!user?.id) return;
    load()
      .catch((err) => setLoadError(err.message || 'Could not load contacts.'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSave = async (values) => {
    if (formState.contact) {
      const updated = await contactService.updateContact(formState.contact.id, values);
      setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      flash('Contact updated.');
    } else {
      const created = await contactService.createContact(user.id, values);
      setContacts((prev) => [created, ...prev]);
      flash('Contact added.');
    }
    setFormState(null);
  };

  const handleDelete = async () => {
    await contactService.deleteContact(toDelete.id);
    setContacts((prev) => prev.filter((c) => c.id !== toDelete.id));
    setToDelete(null);
    flash('Contact deleted.');
  };

  const handleMerge = async (keepId, removeId) => {
    await contactService.mergeContacts(keepId, removeId);
    await load();
    setToResolve(null);
    flash('Contacts merged.');
  };

  const s = search.trim().toLowerCase();
  const filtered = contacts.filter((c) => {
    const isConnection = (stats.get(c.id)?.count || 0) > 0;
    if (filter === 'connections' && !isConnection) return false;
    if (filter === 'contacts' && isConnection) return false;
    return (
      !s ||
      (c.full_name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s) ||
      (c.company || '').toLowerCase().includes(s)
    );
  });

  const connectionTotal = contacts.filter((c) => (stats.get(c.id)?.count || 0) > 0).length;
  const chips = [
    { id: 'all', label: `All (${contacts.length})` },
    { id: 'connections', label: `Connections (${connectionTotal})` },
    { id: 'contacts', label: `Contacts only (${contacts.length - connectionTotal})` }
  ];

  if (loading) return <div className="flex justify-center py-32"><Spinner size="lg" /></div>;

  return (
    <div className="max-w-6xl pb-16">
      {formState && (
        <ContactFormModal contact={formState.contact} onSave={handleSave} onClose={() => setFormState(null)} />
      )}
      {toDelete && (
        <DeleteContactModal contact={toDelete} onConfirm={handleDelete} onClose={() => setToDelete(null)} />
      )}
      {toResolve && (
        <MergeContactsModal
          contact={toResolve}
          matches={findMatches(toResolve, contacts)}
          onMerge={handleMerge}
          onClose={() => setToResolve(null)}
        />
      )}
      {followUpFor && (
        <FollowUpModal
          person={{ contactId: followUpFor.id, name: followUpFor.full_name || 'Unnamed contact' }}
          onClose={() => setFollowUpFor(null)}
          onSaved={(row, text) => {
            setFollowUpFor(null);
            flash(text);
          }}
        />
      )}

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Contacts</h1>
          <p className="mt-2 text-[15px] font-medium text-slate-500">
            Everyone you know, kept private to you. A contact becomes a connection when you add a Moment with them.
          </p>
        </div>
        <Button onClick={() => setFormState({ contact: null })}>
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> Add contact
        </Button>
      </header>

      {success && <Alert variant="success" message={success} className="mb-6" />}
      {loadError && <Alert variant="error" title="Could not load contacts" message={loadError} className="mb-6" />}

      {contacts.length > 0 && (
        <>
          <div className="relative mb-4">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, email or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11"
            />
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {chips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => setFilter(chip.id)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  filter === chip.id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </>
      )}

      {filtered.length === 0 && !loadError ? (
        <div className="bg-white rounded-[2rem] border border-slate-200/60 border-dashed p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-slate-100">
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2 tracking-tight">
            {contacts.length === 0 ? 'No contacts yet' : 'No matching contacts'}
          </h3>
          <p className="text-[15px] font-medium text-slate-500 max-w-sm mx-auto">
            {contacts.length === 0 ? 'Add your first contact to get started.' : 'Try a different search or filter.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              momentCount={stats.get(c.id)?.count || 0}
              fromCard={cardIds.has(c.id)}
              hasDuplicates={findMatches(c, contacts).length > 0}
              onResolve={setToResolve}
              onEdit={(contact) => setFormState({ contact })}
              onDelete={setToDelete}
              onFollowUp={setFollowUpFor}
            />
          ))}
        </div>
      )}
    </div>
  );
};